/**
 * Copyright (c) 2025 Répét Contributors
 * Licensed under the MIT License
 * See LICENSE file in the project root for full license text
 */

import { db } from './database'
import type { GroupMember, GroupText } from '../models/Group'

// ============================================================================
// Types serveur (snapshot)
// ============================================================================

export interface ServerGroupData {
  id: string
  name: string
  description?: string
  ownerId: string
  createdAt: string
  updatedAt: string
}

export interface ServerGroupMemberData {
  id: string
  groupId: string
  userId: string
  role: 'OWNER' | 'EDITOR' | 'VIEWER'
  joinedAt: string
  // Données dénormalisées pour l'affichage offline
  userEmail?: string
  userName?: string
}

export interface ServerGroupTextData {
  id: string
  groupId: string
  textId: string
  versionId: string
  publishedById: string
  publishedAt: string
  // Données dénormalisées pour l'affichage offline
  publishedByName?: string
  textTitle?: string
  textAuthor?: string
  versionLabel?: string
  ownerDisplayName?: string
  hasAudio?: boolean
  audioDuration?: number
}

// ============================================================================
// Upsert depuis le serveur
// ============================================================================

export async function upsertGroupFromServer(data: ServerGroupData): Promise<void> {
  const existing = await db.groups.get(data.id)
  if (existing) {
    await db.groups.update(data.id, {
      name: data.name,
      description: data.description,
      ownerId: data.ownerId,
      updatedAt: data.updatedAt,
      syncStatus: 'SYNCED',
    })
  } else {
    await db.groups.add({
      id: data.id,
      name: data.name,
      description: data.description,
      ownerId: data.ownerId,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      syncStatus: 'SYNCED',
      serverId: data.id,
    })
  }
}

export async function upsertGroupMemberFromServer(data: ServerGroupMemberData): Promise<void> {
  const existing = await db.groupMembers.get(data.id)
  if (existing) {
    await db.groupMembers.update(data.id, {
      role: data.role,
      joinedAt: data.joinedAt,
      // Données dénormalisées (peuvent évoluer si l'utilisateur change de nom)
      ...(data.userEmail !== undefined && { userEmail: data.userEmail }),
      ...(data.userName !== undefined && { userName: data.userName }),
    })
  } else {
    const record: GroupMember & { userEmail?: string; userName?: string } = {
      id: data.id,
      groupId: data.groupId,
      userId: data.userId,
      role: data.role,
      joinedAt: data.joinedAt,
      syncStatus: 'SYNCED',
      serverId: data.id,
    }
    if (data.userEmail !== undefined) record.userEmail = data.userEmail
    if (data.userName !== undefined) record.userName = data.userName
    await db.groupMembers.add(record)
  }
}

export async function upsertGroupTextFromServer(data: ServerGroupTextData): Promise<void> {
  const existing = await db.groupTexts.get(data.id)
  if (existing) {
    await db.groupTexts.update(data.id, {
      versionId: data.versionId,
      publishedById: data.publishedById,
      publishedAt: data.publishedAt,
      ...(data.publishedByName !== undefined && { publishedByName: data.publishedByName }),
      ...(data.textTitle !== undefined && { textTitle: data.textTitle }),
      ...(data.textAuthor !== undefined && { textAuthor: data.textAuthor }),
      ...(data.versionLabel !== undefined && { versionLabel: data.versionLabel }),
      ...(data.ownerDisplayName !== undefined && { ownerDisplayName: data.ownerDisplayName }),
      ...(data.hasAudio !== undefined && { hasAudio: data.hasAudio }),
      ...(data.audioDuration !== undefined && { audioDuration: data.audioDuration }),
      syncStatus: 'SYNCED',
    })
  } else {
    const record: GroupText & {
      publishedByName?: string
      textTitle?: string
      textAuthor?: string
      versionLabel?: string
      ownerDisplayName?: string
      hasAudio?: boolean
      audioDuration?: number
    } = {
      id: data.id,
      groupId: data.groupId,
      textId: data.textId,
      versionId: data.versionId,
      publishedById: data.publishedById,
      publishedAt: data.publishedAt,
      syncStatus: 'SYNCED',
      serverId: data.id,
    }
    if (data.publishedByName !== undefined) record.publishedByName = data.publishedByName
    if (data.textTitle !== undefined) record.textTitle = data.textTitle
    if (data.textAuthor !== undefined) record.textAuthor = data.textAuthor
    if (data.versionLabel !== undefined) record.versionLabel = data.versionLabel
    if (data.ownerDisplayName !== undefined) record.ownerDisplayName = data.ownerDisplayName
    if (data.hasAudio !== undefined) record.hasAudio = data.hasAudio
    if (data.audioDuration !== undefined) record.audioDuration = data.audioDuration
    await db.groupTexts.add(record)
  }
}

// ============================================================================
// Lecture locale (offline-first)
// ============================================================================

/**
 * Retourne les groupes dont l'utilisateur est membre (via groupMembers).
 * Construit une vue dénormalisée compatible avec GroupResponse du serveur.
 */
export async function getLocalGroups(userId: string): Promise<LocalGroupView[]> {
  // Récupérer les memberships de l'utilisateur
  const memberships = await db.groupMembers.where('userId').equals(userId).toArray()
  const groupIds = memberships.map((m) => m.groupId)

  if (groupIds.length === 0) return []

  const groups = await db.groups.where('id').anyOf(groupIds).toArray()

  return groups.map((group) => {
    const membership = memberships.find((m) => m.groupId === group.id)
    // Compter les membres du groupe
    return {
      id: group.id,
      name: group.name,
      description: group.description,
      ownerId: group.ownerId,
      createdAt: group.createdAt,
      updatedAt: group.updatedAt,
      userRole: membership?.role,
      syncStatus: group.syncStatus,
    }
  })
}

export interface LocalGroupView {
  id: string
  name: string
  description?: string
  ownerId: string
  createdAt: string
  updatedAt: string
  userRole?: 'OWNER' | 'EDITOR' | 'VIEWER'
  syncStatus?: 'PENDING' | 'SYNCED'
}

/**
 * Retourne les membres d'un groupe avec leurs infos dénormalisées.
 */
export async function getLocalGroupMembers(groupId: string): Promise<LocalGroupMemberView[]> {
  const members = await db.groupMembers.where('groupId').equals(groupId).toArray()
  return members.map((m) => {
    const extended = m as GroupMember & { userEmail?: string; userName?: string }
    return {
      id: m.id,
      groupId: m.groupId,
      userId: m.userId,
      role: m.role,
      joinedAt: m.joinedAt,
      userEmail: extended.userEmail ?? '',
      userName: extended.userName ?? '',
    }
  })
}

export interface LocalGroupMemberView {
  id: string
  groupId: string
  userId: string
  role: 'OWNER' | 'EDITOR' | 'VIEWER'
  joinedAt: string
  userEmail: string
  userName: string
}

/**
 * Retourne les textes partagés dans un groupe avec leurs infos dénormalisées.
 */
export async function getLocalGroupTexts(groupId: string): Promise<LocalGroupTextView[]> {
  const groupTexts = await db.groupTexts.where('groupId').equals(groupId).toArray()
  return groupTexts.map((gt) => {
    const extended = gt as GroupText & {
      publishedByName?: string
      textTitle?: string
      textAuthor?: string
      versionLabel?: string
      ownerDisplayName?: string
      hasAudio?: boolean
      audioDuration?: number
    }
    return {
      groupTextId: gt.id,
      groupId: gt.groupId,
      textId: gt.textId,
      versionId: gt.versionId,
      publishedById: gt.publishedById,
      publishedAt: gt.publishedAt,
      publishedByName: extended.publishedByName ?? '',
      textTitle: extended.textTitle ?? '',
      textAuthor: extended.textAuthor,
      versionLabel: extended.versionLabel,
      ownerDisplayName: extended.ownerDisplayName,
      hasAudio: extended.hasAudio ?? false,
      audioDuration: extended.audioDuration,
    }
  })
}

export interface LocalGroupTextView {
  groupTextId: string
  groupId: string
  textId: string
  versionId: string
  publishedById: string
  publishedAt: string
  publishedByName: string
  textTitle: string
  textAuthor?: string
  versionLabel?: string
  ownerDisplayName?: string
  hasAudio: boolean
  audioDuration?: number
}

/**
 * Retourne les groupes dans lesquels un texte est partagé.
 */
export async function getLocalGroupsForText(textId: string): Promise<LocalGroupsForTextEntry[]> {
  const entries = await db.groupTexts.where('textId').equals(textId).toArray()
  const results: LocalGroupsForTextEntry[] = []

  for (const entry of entries) {
    const group = await db.groups.get(entry.groupId)
    if (!group) continue
    results.push({
      groupTextId: entry.id,
      groupId: entry.groupId,
      groupName: group.name,
      publishedAt: entry.publishedAt,
    })
  }

  return results
}

export interface LocalGroupsForTextEntry {
  groupTextId: string
  groupId: string
  groupName: string
  publishedAt: string
}

/**
 * Retourne un groupe local avec ses membres — équivalent de GroupDetailResponse.
 * Retourne null si non trouvé localement.
 */
export async function getLocalGroupDetail(groupId: string): Promise<LocalGroupDetailView | null> {
  const group = await db.groups.get(groupId)
  if (!group) return null

  const members = await getLocalGroupMembers(groupId)

  return {
    id: group.id,
    name: group.name,
    description: group.description,
    ownerId: group.ownerId,
    createdAt: group.createdAt,
    updatedAt: group.updatedAt,
    syncStatus: group.syncStatus,
    members,
  }
}

export interface LocalGroupDetailView extends LocalGroupView {
  members: LocalGroupMemberView[]
}

// ============================================================================
// Suppression locale
// ============================================================================

/**
 * Supprime un groupe et toutes ses entités liées (membres, textes partagés).
 */
export async function deleteGroupLocally(groupId: string): Promise<void> {
  await db.transaction('rw', [db.groups, db.groupMembers, db.groupTexts], async () => {
    await db.groupMembers.where('groupId').equals(groupId).delete()
    await db.groupTexts.where('groupId').equals(groupId).delete()
    await db.groups.delete(groupId)
  })
}

/**
 * Supprime un texte partagé localement (par groupTextId).
 */
export async function deleteGroupTextLocally(groupTextId: string): Promise<void> {
  await db.groupTexts.delete(groupTextId)
}

/**
 * Supprime un membre localement.
 */
export async function deleteGroupMemberLocally(memberId: string): Promise<void> {
  await db.groupMembers.delete(memberId)
}

/**
 * Purge les groupes dont l'utilisateur n'est plus membre.
 * Appelé lors d'un premier sync (snapshot exhaustif).
 * groupIds : ensemble des IDs de groupes retournés par le snapshot.
 */
export async function purgeStaleGroups(userId: string, groupIds: Set<string>): Promise<void> {
  const memberships = await db.groupMembers.where('userId').equals(userId).toArray()
  const localGroupIds = new Set(memberships.map((m) => m.groupId))

  for (const localGroupId of localGroupIds) {
    if (!groupIds.has(localGroupId)) {
      await deleteGroupLocally(localGroupId)
    }
  }
}

/**
 * Purge les membres d'un groupe qui ne sont plus dans le snapshot.
 * memberIds : ensemble des IDs de membres retournés par le snapshot pour ce groupe.
 */
export async function purgeStaleGroupMembers(
  groupId: string,
  memberIds: Set<string>
): Promise<void> {
  const localMembers = await db.groupMembers.where('groupId').equals(groupId).toArray()
  for (const m of localMembers) {
    if (!memberIds.has(m.id)) {
      await db.groupMembers.delete(m.id)
    }
  }
}

/**
 * Purge les textes partagés d'un groupe qui ne sont plus dans le snapshot.
 * groupTextIds : ensemble des IDs de groupTexts retournés par le snapshot pour ce groupe.
 */
export async function purgeStaleGroupTexts(
  groupId: string,
  groupTextIds: Set<string>
): Promise<void> {
  const localTexts = await db.groupTexts.where('groupId').equals(groupId).toArray()
  for (const gt of localTexts) {
    if (!groupTextIds.has(gt.id)) {
      await db.groupTexts.delete(gt.id)
    }
  }
}
